/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { forwardRef } from 'react';

type OwnProps = {
  hint?: React.ReactNode;
  id?: string;
  innerRef?: React.Ref<HTMLInputElement>;
  label?: string;
  name: string;
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  onInvalid?: () => void;
  required?: boolean;
  suffix?: React.ReactNode;
  type?: 'email' | 'password' | 'text';
  autoComplete?:
    | 'email'
    | 'password'
    | 'one-time-code'
    | 'organization'
    | 'current-password'
    | 'new-password'
    | 'name'
    | 'given-name'
    | 'family-name';
};

export type Props = OwnProps & Omit<React.ComponentPropsWithRef<'input'>, keyof OwnProps>;

function normalize(id: string) {
  return (
    id
      .trim()
      .toLowerCase()
      // Remove all non-alphanumeric or spaces characters
      .replace(/[^a-z0-9_\- ]/gi, '')
      // Replace all missing non-alphanumeric by hyphens
      .replace(/\W/g, '-')
  );
}

const styles = {
  container: css`
    background: #fff;
    display: flex;
    border-radius: 2px;
    border: 1px solid #636363;
    font-size: 16px;
    line-height: 22px;
    transition: border-color 250ms ease-out;

    &:focus-within {
      border-color: #2a73cc;
    }
  `,

  label: css`
    color: #1f1f1f;
    display: block;
    font-size: 12px;
    line-height: 18px;
    margin-bottom: 8px;
    text-transform: uppercase;
  `,
  hint: css`
    bottom: 0;
    color: #616161;
    font-size: 12px;
    left: 0;
    line-height: 18px;
    margin-top: 8px;
    position: absolute;
    width: 100%;
  `,
  input: css`
    background: #fff;
    border-radius: 2px;
    border: 0;
    flex-grow: 1;
    font-size: 16px;
    line-height: 22px;
    outline: none;
    padding: 12px 16px;
    width: 100%;
    color: var(--cds-color-neutral-primary);

    &::placeholder {
      color: var(--cds-color-neutral-primary-weak);
      transition: color 250ms ease-out;
      opacity: 1;
    }
  `,
  root: css`
    position: relative;
  `,
  withHint: css`
    padding-bottom: 26px;
  `,
};

const TextInput = forwardRef<HTMLInputElement, Props>(
  (
    { className, hint, id, label, name, required = false, type = 'text', suffix, style, autoComplete, ...props },
    ref
  ) => {
    const htmlFor = normalize(id || name);

    return (
      <div className={className} css={[styles.root, hint && styles.withHint]} style={style}>
        {label && (
          <label css={styles.label} htmlFor={htmlFor}>
            {label}
          </label>
        )}
        <div css={styles.container}>
          <input
            {...(hint && { 'aria-describedby': `${htmlFor}-hint` })}
            {...props}
            aria-required={required}
            css={styles.input}
            id={htmlFor}
            name={name}
            ref={ref}
            type={type}
            required={required}
            autoComplete={autoComplete}
          />
          {suffix}
        </div>
        {hint && (
          <div css={styles.hint} id={`${htmlFor}-hint`}>
            {hint}
          </div>
        )}
      </div>
    );
  }
);

export default TextInput;
